/* Merge history panel — audit trail of every merge that touched this lead.
 *
 * Shown on the Lead Report page so wholesalers can see exactly how their
 * record was assembled: which fields were filled, whether notes were
 * appended, how many photos came across, and what the in-progress address
 * looked like before the merge. Hides itself when there's nothing to show
 * (most leads).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, MessageSquarePlus, Image as ImageIcon, Loader2, Undo2 } from "lucide-react";

const FIELD_LABELS = {
  city: "City",
  state: "State",
  zip_code: "ZIP",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  sqft: "Sqft",
  asking_price: "Asking",
  estimated_arv: "ARV",
  estimated_repairs: "Repairs",
  seller_name: "Seller name",
  seller_phone: "Seller phone",
  seller_email: "Seller email",
  lead_source: "Lead source",
};

function MergeRow({ event, leadId, canUndo, onUndone }) {
  const ts = event.merged_at ? new Date(event.merged_at).toLocaleString() : "";
  const fieldLabels = (event.filled_fields || []).map((k) => FIELD_LABELS[k] || k);
  const [undoing, setUndoing] = useState(false);

  const summary = (() => {
    const parts = [];
    if (fieldLabels.length) parts.push(`reset ${fieldLabels.length} filled field${fieldLabels.length === 1 ? "" : "s"}`);
    if (event.notes_appended) parts.push("strip the merged note");
    if (event.photos_added > 0) parts.push(`remove ${event.photos_added} photo${event.photos_added === 1 ? "" : "s"}`);
    return parts.join(", ");
  })();

  const undo = async () => {
    if (!window.confirm(`Undo this merge? Will ${summary || "delete the audit row"}.`)) return;
    setUndoing(true);
    try {
      await api.post(`/leads/${leadId}/merge-history/${event.id}/undo`);
      toast.success("Merge undone");
      onUndone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't undo");
      setUndoing(false);
    }
  };

  return (
    <li
      className="card-flat p-3 flex items-start gap-3"
      data-testid={`merge-history-row-${event.id}`}
    >
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex-shrink-0">
        <GitMerge size={14} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--ink-3)] font-mono">{ts}</div>
        {event.source_address && (
          <div className="text-xs text-[var(--ink-2)] mt-0.5">
            From typing <span className="font-mono">&ldquo;{event.source_address}&rdquo;</span>
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {fieldLabels.length > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
              Filled: {fieldLabels.join(", ")}
            </span>
          )}
          {event.notes_appended && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 text-[11px] font-semibold">
              <MessageSquarePlus size={10} /> Notes appended
            </span>
          )}
          {event.photos_added > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700 text-[11px] font-semibold">
              <ImageIcon size={10} /> {event.photos_added} photo{event.photos_added === 1 ? "" : "s"} added
            </span>
          )}
          {fieldLabels.length === 0 && !event.notes_appended && event.photos_added === 0 && (
            <span className="text-xs text-[var(--ink-3)] italic">No net changes (idempotent)</span>
          )}
        </div>
      </div>
      {canUndo && (
        <button
          type="button"
          onClick={undo}
          disabled={undoing}
          className="self-start inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
          title="Reverse this merge — only the most recent merge can be undone"
          data-testid={`merge-history-undo-${event.id}`}
        >
          {undoing ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
          {undoing ? "Undoing…" : "Undo"}
        </button>
      )}
    </li>
  );
}

export default function MergeHistoryPanel({ leadId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!leadId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}/merge-history`);
        if (active) setEvents(data?.items || []);
      } catch {
        // Silent — render nothing rather than a noisy error card.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [leadId, reloadKey]);

  // Hide the whole panel when there's nothing to show — most leads will
  // never have a merge event, and a "0 merges" card would be noise.
  if (loading) return null;
  if (events.length === 0) return null;

  return (
    <div className="card mb-5 print:hidden" data-testid="merge-history-panel">
      <div className="flex items-center gap-2 mb-3">
        <GitMerge size={16} className="text-violet-700" />
        <span className="font-display font-bold text-base">Merge history</span>
        <span className="text-xs text-[var(--ink-3)] tabular-nums">· {events.length}</span>
      </div>
      <p className="text-xs text-[var(--ink-2)] mb-3">
        This lead has been assembled from {events.length} duplicate-detection merge{events.length === 1 ? "" : "s"}.
      </p>
      {loading ? (
        <div className="text-center text-[var(--ink-3)] text-sm py-4">
          <Loader2 size={14} className="animate-spin inline mr-1" /> Loading…
        </div>
      ) : (
        <ul className="space-y-2">
          {events.map((event, idx) => (
            <MergeRow
              key={event.id}
              event={event}
              leadId={leadId}
              canUndo={idx === 0}
              onUndone={() => setReloadKey((k) => k + 1)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
